import { Component } from "@angular/core";
import { DynamicDialogConfig, DynamicDialogRef } from "primeng/dynamicdialog";
import { TransactionsService } from "../_services/transactions.service";
import { MessageService } from "primeng/api";
import { firstValueFrom } from "rxjs";
import { IParamsCollection } from "src/app/core/jsonapi/interfaces/params-collection";
import { FilterBuilder } from "src/app/core/jsonapi/filters-builder";
import { Filter, FilterOperation } from "src/app/core/jsonapi/filter";
import { TransactionsTypesService } from "../_services/transactions-types.service";
import { Contract } from "../../companies/_types/contract";
import { ContractTransactionSeriesService } from "../../companies/_services/contracts-transactions-series.service";
import { PaymentsMethodsService } from "../../common/_services/payments-methods.service";
import { PaymentMethod } from "../../common/_types/payment-method";
import { BusinessBankAccountsService } from "../../business/_services/business-bank-accounts.service";
import { BusinessBankAccount } from "../../business/_types/business-bank-account";
import { Transaction } from "../_types/transaction";

@Component({
    selector: 'app-generate-invoice',
    templateUrl: './generate-invoice.component.html',
    styleUrls: ['./generate-invoice.component.scss'],
    standalone: false
})
export class GenerateInvoiceComponent {

  private selectedRowsIds: any[] = [];

  public transactionSeries: any[] = [];
  public paymentsMethods: PaymentMethod[] = [];
  public billingCompaniesBanksAccounts: BusinessBankAccount[] = [];
  public transactions: Transaction[] = [];
  private contract: Contract | null = null;
  public selectedPaymentMethodRequiresBank: boolean = false;
  public totalTransactionsSelected: number = 0;
  public subtotalTransactionsSelected: number = 0;

  public invoice: any = {
    date: new Date(),
    isPayed: false
  };

  constructor(
    private config: DynamicDialogConfig,
    private dialogRef: DynamicDialogRef,
    private transactionsService: TransactionsService,
    private transactionsTypesService: TransactionsTypesService,
    private contractTransactionSeriesService: ContractTransactionSeriesService,
    private paymentsMethodsService: PaymentsMethodsService,
    private businessBankAccountsService: BusinessBankAccountsService,
    private messageService: MessageService
  ) {
    this.selectedRowsIds = this.config.data.selectedRowsIds;

    //cargamos los datos de las transacciones a facturar
    this.loadTransactions();
  }

  async loadTransactions(){
    let params: IParamsCollection = {
      include: ['case', 'case.contract']
    };
    params.filter = FilterBuilder.fromObject({ id: new Filter(FilterOperation.any, 'id', '', this.selectedRowsIds.map(m => m.id)) }).build();

    let response = await firstValueFrom(this.transactionsService.all(params));
    this.transactions = response.data;
    this.totalTransactionsSelected = this.transactions.map(m => m.total).reduce((a, b) => a + b, 0);
    this.subtotalTransactionsSelected = this.transactions.map(m => m.subtotal).reduce((a, b) => a + b, 0);
    //asignamos el contrato de la primera transacción
    if (this.transactions.length > 0) {
      this.contract = this.transactions[0].case?.contract ?? null;

      //cargamos series
      await this.loadTransactionSeries();

      //cargamos formas de pago
      await this.loadPaymentsMethods();

      //cargamos cuentas bancarias de la empresa de facturación
      await this.loadBillingCompaniesBankAccounts();
      
      //preparamos datos de la factura
      await this.prepareInvoiceData();
    }

  }

  async loadTransactionSeries() {
    //Obtenemos el id del tipo de transacición factura de empresa
    let paramsTransactionType: IParamsCollection = {
      include: []
    }
    //creamos filtros
    paramsTransactionType.filter = FilterBuilder.fromObject({ code: new Filter(FilterOperation.equals, 'code', '', "company_outgoing_invoice") }).build();

    let responseTransactionType = await firstValueFrom(this.transactionsTypesService.all(paramsTransactionType));
    let transactionTypeInvoice = responseTransactionType.data[0];

    let filters: any = {};
    if (this.contract != null) {
      filters.contractId = new Filter(FilterOperation.equals, 'contractId', "", this.contract.id);
    }

    filters.transactionTypeId = new Filter(FilterOperation.equals, 'transactionSerie.transactionTypeId', "", transactionTypeInvoice.id);

    let params: IParamsCollection = {
      page: { size: 0 },
      include: ['transactionSerie'],
      filter: FilterBuilder.fromObject(filters).build()
    };
    let response = await firstValueFrom(this.contractTransactionSeriesService.all(params));
    this.transactionSeries = response.data.map((m: any) => {
      return m.transactionSerie;
    });
    this.transactionSeries.push({id: -1, name: "Sin serie"});
    
  }

  async loadPaymentsMethods() {
    let response = await firstValueFrom(this.paymentsMethodsService.all({ page: { number: 1, size: 10 } }));
    this.paymentsMethods = response.data;
  }

  async loadBillingCompaniesBankAccounts() {

    let filters: any = {};
    if (this.contract?.billingCompanyId != null) {
      filters.companyId = new Filter(FilterOperation.equals, 'businessId', "", this.contract.billingCompanyId);
    }

    let params: IParamsCollection = {
      page: { size: 0 },
      filter: FilterBuilder.fromObject(filters).build()
    };
    let response = await firstValueFrom(this.businessBankAccountsService.all(params));
    this.billingCompaniesBanksAccounts = response.data;
  }

  async prepareInvoiceData(){
    this.invoice = {};
    //Obtenemos los datos del expediente para tener el contrato            
    this.invoice.paymentDuedateDays = this.contract?.paymentDuedateDays;
    this.invoice.paymentMethodId = this.contract?.paymentMethodId;
    this.onPaymentMethodChange(this.invoice.paymentMethodId);
    this.invoice.billingCompanyBankAccountId = this.contract?.billingCompanyBankAccountId;
    this.invoice.date = new Date();
    //Añadimos a fecha los dias de vencimiento de contrato
    this.invoice.paymentDuedate = new Date(this.invoice.date);
    this.invoice.paymentDuedate.setDate(this.invoice.paymentDuedate.getDate() + this.contract?.paymentDuedateDays);       
    if(this.transactionSeries.length>0) this.invoice.transactionSerieId = this.transactionSeries[0].id; 
  }

  onPaymentMethodChange(paymentMethodId: string): void {
    const selectedMethod = this.paymentsMethods.find(m => m.id === paymentMethodId);
    this.selectedPaymentMethodRequiresBank = selectedMethod?.businessBankAccountEnabled === true;
  }

  onInvoiceDateChange(date: Date): void {
    const daysToAdd = this.invoice.paymentDuedateDays; // O cualquier valor que necesites
    if (date) {
      const dueDate = new Date(date);
      if(daysToAdd) dueDate.setDate(dueDate.getDate() + daysToAdd);
      this.invoice.paymentDuedate = dueDate;
    }
  }

  async generateInvoice(){        

    if ((!this.invoice.transactionSerieId && (this.invoice.code == null || this.invoice.code.trim() ==""))||(this.invoice.transactionSerieId == -1 && (this.invoice.code == null || this.invoice.code.trim() ==""))) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Debe seleccionar una serie de factura o añadir un código manual.'
      });
      return;
    }

    let selectedIds = this.selectedRowsIds.map(t => t.id)
    let selectedTransactions = this.transactions.filter((t:any) => selectedIds.includes(t.id));    
          
    let data = {
      transactionSerieId: this.invoice.transactionSerieId != -1 ? this.invoice.transactionSerieId : null,
      date: this.invoice.date.toISOString(),    
      isPayed: this.invoice.isPayed,
      paymentMethodId: this.invoice.paymentMethodId,
      billingCompanyBankAccountId: this.invoice.billingCompanyBankAccountId,
      paymentDuedate: this.invoice.paymentDuedate.toISOString(),           
      deliveryNotesIds: selectedIds,      
      code: this.invoice.code ? this.invoice.code.trim() : null,  
    }              
        
    let result:any = await firstValueFrom(this.transactionsService.generateInvoice(selectedTransactions[0].thirdPartyId, data));      
    
    if (!result.success) {
      this.messageService.add({ closable: true, severity: 'error', detail: result.error.title });      
    } else {
      this.messageService.add({ closable: true, severity: 'success', summary: "Correcto", detail: "Factura generada correctamente." });
      this.dialogRef.close(true);
    }

  }

  async close(){
    this.dialogRef.close();
  }
  

}
